import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Award, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Care',
      description: 'We treat every space with the attention and respect it deserves, as if it were our own.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Consistently delivering the highest standards of cleanliness and professionalism.'
    },
    {
      icon: Users,
      title: 'Reliability',
      description: 'Dependable service you can trust, every time, without exception.'
    }
  ];

  return (
    <div>
      <SEO
        title="About Us"
        description="Learn about Serene Sparkle - London's trusted cleaning service. Our mission, values, and commitment to creating clean, peaceful spaces."
        keywords="about Serene Sparkle, cleaning company London, professional cleaners"
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sky-50 to-emerald-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">About Serene Sparkle</h1>
            <p className="text-xl text-slate-600">
              Creating safe, peaceful spaces through exceptional cleaning services
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Story</h2>
                <div className="space-y-4 text-slate-600">
                  <p>
                    Cleaning has been a part of my life since childhood. I learned techniques and values from my mother, who was a professional cleaner, instilling in me the importance of creating clean, welcoming spaces.
                  </p>
                  <p>
                    Over the years, I refined my skills working in five-star hotels and medical settings, where cleanliness isn't just important—it's essential. These experiences taught me that a truly clean space does more than look good; it creates an environment where people feel safe, comfortable, and at peace.
                  </p>
                  <p>
                    This understanding inspired me to create Serene Sparkle, a service dedicated to bringing that same level of care and attention to homes and businesses across our community.
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1554995207-c18c203602cb?w=800&q=75&auto=format"
                  alt="Clean, peaceful living space by Serene Sparkle"
                  className="rounded-lg shadow-xl"
                  loading="lazy"
                  width="800"
                  height="600"
                />
                <div className="absolute -bottom-6 -right-6 bg-sky-600 text-white p-6 rounded-lg shadow-lg">
                  <p className="text-3xl font-bold">15+</p>
                  <p className="text-sm">Years Experience</p>
                </div>
              </div>
            </div>

            {/* Mission Statement */}
            <div className="bg-gradient-to-br from-sky-50 to-emerald-50 rounded-lg p-8 mb-16">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 text-center">Our Mission</h3>
              <p className="text-lg text-slate-700 text-center max-w-3xl mx-auto">
                To provide exceptional cleaning services that create spaces where people feel safe, comfortable, and at peace. We believe a clean environment is the foundation for a calm mind and a fulfilling life.
              </p>
            </div>

            {/* Values */}
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Values</h3>
              <div className="grid md:grid-cols-3 gap-8">
                {values.map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <Card key={index} className="border-sky-100 hover:shadow-lg transition-shadow">
                      <CardContent className="pt-6 text-center">
                        <div className="bg-gradient-to-br from-sky-100 to-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon className="h-8 w-8 text-sky-600" />
                        </div>
                        <h4 className="text-xl font-semibold text-slate-800 mb-3">{value.title}</h4>
                        <p className="text-slate-600">{value.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-sky-100 to-emerald-100">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Experience the Serene Sparkle Difference
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Let us bring peace and cleanliness to your space. Request a free quote today.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-sky-600 hover:bg-sky-700 text-white shadow-lg hover:shadow-xl transition-all">
              Get a Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
